import { supabase } from "../lib/supabaseClient";

const PAYMENTS_TABLE = "payments";
const PENDING_GENERATION_KEY_PREFIX = "badgeflow-pending-generation:";

const requireClient = () => {
  if (!supabase) throw new Error("Supabase is not configured.");
  return supabase;
};

const fromPaymentRow = (row) => ({
  id: row.id,
  eventId: row.event_id,
  userId: row.user_id,
  amountCents: row.amount_cents,
  currency: row.currency,
  attendeeCount: row.attendee_count,
  chargedAttendeeCount: row.charged_attendee_count,
  badgeCreditPurchasedCount: row.badge_credit_purchased_count || 0,
  badgeCreditUsedCount: row.badge_credit_used_count || 0,
  spreadsheetData: row.spreadsheet_data,
  fieldMapping: row.field_mapping || {},
  status: row.status,
  stripeCheckoutSessionId: row.stripe_checkout_session_id,
  paidAt: row.paid_at,
  createdAt: row.created_at,
});

const postPaymentFunction = async (name, body) => {
  const client = requireClient();
  const { data, error } = await client.functions.invoke(name, { body });

  if (error) {
    let message = error.message;
    try {
      const errorBody = await error.context?.clone?.().json();
      message = errorBody?.error || message;
    } catch {
      // Keep the Supabase Functions error message when the response has no JSON body.
    }
    throw new Error(message || "The payment request could not be completed.");
  }

  return data;
};

export const listPaidBadgeSets = async (eventId, userId) => {
  const client = requireClient();
  const { data, error } = await client
    .from(PAYMENTS_TABLE)
    .select("*")
    .eq("event_id", eventId)
    .eq("user_id", userId)
    .eq("status", "paid")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data || []).map(fromPaymentRow);
};

export const hasPaidBadgeSets = async (eventId, userId) => {
  const client = requireClient();
  const { data, error } = await client
    .from(PAYMENTS_TABLE)
    .select("id")
    .eq("event_id", eventId)
    .eq("user_id", userId)
    .eq("status", "paid")
    .limit(1);

  if (error) throw error;
  return (data || []).length > 0;
};

export const listPaidBadgeCredits = async (userId) => {
  const client = requireClient();
  const { data, error } = await client
    .from(PAYMENTS_TABLE)
    .select("badge_credit_purchased_count,badge_credit_used_count,status")
    .eq("user_id", userId)
    .eq("status", "paid");

  if (error) throw error;
  return data || [];
};

export const beginBadgePurchase = ({
  eventId,
  fieldMapping,
  spreadsheetData,
}) =>
  postPaymentFunction(
    "create-checkout-session",
    { eventId, fieldMapping, spreadsheetData }
  );

export const confirmBadgePurchase = ({ sessionId }) =>
  postPaymentFunction(
    "confirm-checkout-session",
    { sessionId }
  );

export const storePendingGeneration = (sessionId, generation) => {
  sessionStorage.setItem(
    `${PENDING_GENERATION_KEY_PREFIX}${sessionId}`,
    JSON.stringify(generation)
  );
};

export const readPendingGeneration = (sessionId) => {
  try {
    const value = sessionStorage.getItem(
      `${PENDING_GENERATION_KEY_PREFIX}${sessionId}`
    );
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

export const clearPendingGeneration = (sessionId) => {
  sessionStorage.removeItem(`${PENDING_GENERATION_KEY_PREFIX}${sessionId}`);
};
